/**
 * NoteSymbolPresenter Component
 *
 * Display-only WebGL renderer for note markers — sets up the source, the
 * composite sprite layer and pushes geojson updates into MapLibre. Consumed by
 * `NoteSymbolLayer` (which adds click + cursor + selection) and by
 * `NoteMiniMap` (which only needs rendering, no interactions).
 *
 * @module components/map/NoteSymbolLayer/NoteSymbolPresenter
 */

import { memo, useEffect, useRef, type RefObject } from 'react';
import type { MapRef } from '@vis.gl/react-maplibre';
import type { GeoJSONSource, Map as MaplibreMap } from 'maplibre-gl';

import {
  getSpriteCache,
  registerSpritesOnMap,
} from '@/components/map/symbolSprites';
import {
  NOTE_LAYER_IDS,
  NOTE_SOURCE_ID,
  noteMarkerPinLayer,
  noteMarkerIconLayer,
} from './styles/noteSymbolLayerStyles';
import { useNoteGeoJson, type RenderableNote } from './hooks/useNoteGeoJson';

const NOTE_LAYER_ORDER = [
  NOTE_LAYER_IDS.MARKER_PIN,
  NOTE_LAYER_IDS.MARKER_ICON,
] as const;

// ===== Types =====

interface NoteSymbolPresenterProps {
  /** Minimal renderable note shape — `NoteMapItem` satisfies it. */
  notes: RenderableNote[];
  mapRef: RefObject<MapRef | null>;
  isMapReady: boolean;
  selectedId?: string | null;
}

// ===== Layer order helpers =====

type LayerSpec = Parameters<MaplibreMap['addLayer']>[0];

function addLayerIfMissing(map: MaplibreMap, spec: LayerSpec): void {
  if (!map.getLayer(spec.id)) map.addLayer(spec);
}

function addNoteLayers(map: MaplibreMap): void {
  addLayerIfMissing(map, {
    ...noteMarkerPinLayer,
    source: NOTE_SOURCE_ID,
  } as LayerSpec);
  addLayerIfMissing(map, {
    ...noteMarkerIconLayer,
    source: NOTE_SOURCE_ID,
  } as LayerSpec);
}

function ensureNoteLayersOnTop(map: MaplibreMap): void {
  const styleLayers = map.getStyle().layers;
  if (!styleLayers) return;

  const topN = styleLayers.slice(-NOTE_LAYER_ORDER.length);
  const alreadyOnTop =
    topN.length === NOTE_LAYER_ORDER.length &&
    topN.every((layer, index) => layer.id === NOTE_LAYER_ORDER[index]);
  if (alreadyOnTop) return;

  for (const layerId of NOTE_LAYER_ORDER) {
    if (map.getLayer(layerId)) map.moveLayer(layerId);
  }
}

function removeNoteLayers(map: MaplibreMap): void {
  for (const layerId of NOTE_LAYER_ORDER) {
    if (map.getLayer(layerId)) map.removeLayer(layerId);
  }
  if (map.getSource(NOTE_SOURCE_ID)) map.removeSource(NOTE_SOURCE_ID);
}

// ===== Component =====

export const NoteSymbolPresenter = memo(function NoteSymbolPresenter({
  notes,
  mapRef,
  isMapReady,
  selectedId = null,
}: NoteSymbolPresenterProps) {
  const geojson = useNoteGeoJson({ notes, selectedId });
  const sourceReady = useRef(false);
  const geojsonRef = useRef(geojson);
  geojsonRef.current = geojson;

  // Imperative setup: sprites → source → layers → reorder.
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !isMapReady) return;

    let cancelled = false;

    const setup = async (): Promise<void> => {
      const sprites = await getSpriteCache();
      if (cancelled) return;

      registerSpritesOnMap(map, sprites);

      if (!map.getSource(NOTE_SOURCE_ID)) {
        map.addSource(NOTE_SOURCE_ID, {
          type: 'geojson',
          data: geojsonRef.current,
        });
      }

      addNoteLayers(map);
      sourceReady.current = true;

      requestAnimationFrame(() => {
        if (!cancelled) ensureNoteLayersOnTop(map);
      });
    };

    if (map.isStyleLoaded()) {
      setup();
    } else {
      map.once('style.load', () => setup());
    }

    const handleStyleLoad = (): void => {
      sourceReady.current = false;
      setup();
    };
    map.on('style.load', handleStyleLoad);

    const handleIdle = (): void => {
      if (sourceReady.current) ensureNoteLayersOnTop(map);
    };
    map.on('idle', handleIdle);

    return () => {
      cancelled = true;
      map.off('style.load', handleStyleLoad);
      map.off('idle', handleIdle);
      try {
        removeNoteLayers(map);
      } catch (error) {
        // Map style may already be torn down (StrictMode unmount, route change).
        if (import.meta.env.DEV) {
          console.warn('NoteSymbolPresenter: removeNoteLayers failed', error);
        }
      }
      sourceReady.current = false;
    };
  }, [mapRef, isMapReady]);

  // Push reactive GeoJSON updates.
  useEffect(() => {
    if (!sourceReady.current) return;
    const map = mapRef.current?.getMap();
    if (!map) return;

    const source = map.getSource(NOTE_SOURCE_ID) as GeoJSONSource | undefined;
    if (source) source.setData(geojson);
  }, [geojson, mapRef]);

  return null;
});
